// ---------------------------------------------------------------------------
// TanStack Query key factory for the V2 knowledge surface.
// Kept in its own module so callers (use-knowledge-documents,
// use-knowledge-aggregate, use-knowledge-folders, …) can import the keys
// without pulling in the heavier hook code.
// ---------------------------------------------------------------------------

import { broadcastKeys } from '@/hooks/broadcasts/broadcast-keys';
import { documentKeys } from '@/hooks/document-keys';
import {
  DRAFT_SOURCE_EVIDENCE_QUERY_KEY,
  KNOWLEDGE_QUERY_KEY,
  SENT_DRAFT_CITATIONS_QUERY_KEY,
} from '@/hooks/pii-query-key-roots';
import { isKnowledgeStorageKey } from '@/hooks/use-storage-file';
import {
  OFFICE_CONTEXT_SCOPE,
  contextScopeSegment,
  type KnowledgeContextScope,
} from '@/lib/knowledge/knowledge-query-scope';
import { stableStringify } from '@/lib/utils/stable-stringify';

/**
 * What the shared Knowledge source picker lists. Only `documents` answers per
 * work context, so only its key carries a context segment (ADR 0091 D3).
 */
export type KnowledgePickerMode = 'documents' | 'engagements';

/**
 * Lists, details and search results are what the viewer's RLS admits (owner-
 * private documents differ per member), so every payload-carrying key holds
 * the actor after the org: two members of one office never share an entry, a
 * restored snapshot from another sign-in cannot match, and `documentListAll(orgId)`
 * stays a valid invalidation prefix (ADR 0091).
 */
export const knowledgeKeys = {
  all: [KNOWLEDGE_QUERY_KEY] as const,
  documents: () => [...knowledgeKeys.all, 'documents'] as const,
  documentList: (orgId: string, actorId: string | null, filterConfig: unknown = null) =>
    [...knowledgeKeys.documents(), orgId, actorId, stableStringify(filterConfig)] as const,
  /**
   * Prefix key for invalidating *every* document-list variant for an org.
   * Use from mutations that may affect rows across filter combinations
   * (folder membership, account connect/disconnect, …) — `documentList(orgId)`
   * is a terminal key and only matches the no-filter cache entry.
   */
  documentListAll: (orgId: string) => [...knowledgeKeys.documents(), orgId] as const,
  documentDetail: (orgId: string, actorId: string | null, docId: string) =>
    [...knowledgeKeys.documents(), 'detail', orgId, actorId, docId] as const,
  /**
   * Prefix key for every viewer's detail of every document of an org. Details
   * sit outside `documentListAll(orgId)`, so a list prefix never reaches them.
   */
  documentDetailAll: (orgId: string) => [...knowledgeKeys.documents(), 'detail', orgId] as const,
  chunkCount: (docId: string) => [...knowledgeKeys.documents(), 'chunk-count', docId] as const,
  /**
   * `userId` is part of the key because the aggregate payload is per-user, not
   * per-org: `favorites_count` is the caller's own, and `smart_folder_counts` /
   * `manual_folder_counts` omit folders private to another member. It sits
   * AFTER `orgId` so `aggregateAll(orgId)` stays a valid invalidation prefix.
   */
  aggregate: (orgId: string, userId: string, filterConfig: unknown = null) =>
    [...knowledgeKeys.all, 'aggregate', orgId, userId, stableStringify(filterConfig)] as const,
  /**
   * Prefix key for invalidating *every* aggregate variant for an org. Use this
   * from mutations that change document or smart-folder state — the FilterBar
   * may have created multiple cached variants under different filterConfig
   * keys, and only a prefix invalidation reaches them all.
   */
  aggregateAll: (orgId: string) => [...knowledgeKeys.all, 'aggregate', orgId] as const,
  profiles: () => [...knowledgeKeys.all, 'profiles'] as const,
  profileList: (orgId: string) => [...knowledgeKeys.profiles(), orgId] as const,
  folders: () => [...knowledgeKeys.all, 'folders'] as const,
  /** Private folders belong to their creator, so the list is per viewer. */
  folderList: (orgId: string, actorId: string | null) =>
    [...knowledgeKeys.folders(), orgId, actorId] as const,
  /** Prefix key for every viewer's copy of one folder's document ids. */
  folderDocumentsAll: (orgId: string, folderId: string) =>
    [...knowledgeKeys.folders(), 'documents', orgId, folderId] as const,
  /** One folder's document ids as this viewer's RLS admits them. */
  folderDocuments: (orgId: string, actorId: string | null, folderId: string) =>
    [...knowledgeKeys.folderDocumentsAll(orgId, folderId), actorId] as const,
  favorites: () => [...knowledgeKeys.all, 'favorites'] as const,
  favoritesByUser: (userId: string) => [...knowledgeKeys.favorites(), userId] as const,
  tags: () => [...knowledgeKeys.all, 'tags'] as const,
  tagList: (orgId: string) => [...knowledgeKeys.tags(), orgId] as const,
  search: () => [...knowledgeKeys.all, 'search'] as const,
  /**
   * A search in a work item's context (ADR 0091 D3) is its own cache entry per
   * tuple: the same query answers differently per client and revision. Office
   * scope adds no segment, so office searches keep exactly the key they had.
   */
  searchResults: (
    orgId: string,
    actorId: string | null,
    query: string,
    context: KnowledgeContextScope = OFFICE_CONTEXT_SCOPE,
  ) =>
    context.kind === 'office'
      ? ([...knowledgeKeys.search(), orgId, actorId, query] as const)
      : ([
          ...knowledgeKeys.search(),
          orgId,
          actorId,
          query,
          ...contextScopeSegment(context),
        ] as const),
  workContexts: () => [...knowledgeKeys.all, 'work-context'] as const,
  /** Prefix key for every work context of an org, for invalidation after a choice or a default. */
  workContextAll: (orgId: string) => [...knowledgeKeys.workContexts(), orgId] as const,
  /** One work item's resolution as this viewer sees it (the resolver answers per caller). */
  workContext: (orgId: string, actorId: string | null, kind: string, id: string) =>
    [...knowledgeKeys.workContextAll(orgId), actorId, kind, id] as const,
  spacesGate: (orgId: string, actorId: string | null) =>
    [...knowledgeKeys.all, 'spaces-gate', orgId, actorId] as const,
  // Knowledge Spaces families (ADR 0091): each answers under the viewer's RLS,
  // so each holds the org at index 2 and the actor at index 3, the shape
  // KNOWLEDGE_FAMILY_MATCHERS selects by.
  /** The spaces directory, one entry per filter set (kind, status, search). */
  spaces: (orgId: string, actorId: string | null, filters: unknown = null) =>
    [...knowledgeKeys.all, 'spaces', orgId, actorId, stableStringify(filters)] as const,
  /** One space with its engagement and active roster. */
  space: (orgId: string, actorId: string | null, spaceId: string) =>
    [...knowledgeKeys.all, 'space', orgId, actorId, spaceId] as const,
  /** A team or client space's memberships. */
  spaceMembers: (orgId: string, actorId: string | null, spaceId: string, filters: unknown = null) =>
    [
      ...knowledgeKeys.all,
      'space-members',
      orgId,
      actorId,
      spaceId,
      stableStringify(filters),
    ] as const,
  /** A space's Drive bindings as the viewer sees them, with their folders' sync state. */
  spaceSources: (orgId: string, actorId: string | null, spaceId: string) =>
    [...knowledgeKeys.all, 'space-sources', orgId, actorId, spaceId] as const,
  /** Which ingest providers the server would accept a binding for; the same for every viewer. */
  ingestReady: () => [...knowledgeKeys.all, 'ingest-ready'] as const,
  /**
   * One page set of a Drive folder's subfolders on the office's connection. Under the knowledge
   * root so the persisted snapshot refuses it: it holds Drive folder names.
   */
  driveFolderPages: (
    orgId: string,
    actorId: string | null,
    connectionId: string | null,
    parentFolderId: string | null,
    searchQuery: string,
  ) =>
    [
      ...knowledgeKeys.all,
      'drive-folder-pages',
      orgId,
      actorId,
      connectionId,
      parentFolderId,
      searchQuery,
    ] as const,
  /** The client identities the viewer may see. */
  engagements: (orgId: string, actorId: string | null, filters: unknown = null) =>
    [...knowledgeKeys.all, 'engagements', orgId, actorId, stableStringify(filters)] as const,
  /** Saved CRM defaults (admins). */
  associations: (orgId: string, actorId: string | null, filters: unknown = null) =>
    [...knowledgeKeys.all, 'associations', orgId, actorId, stableStringify(filters)] as const,
  /** The access audit (admins). */
  accessAudit: (orgId: string, actorId: string | null, filters: unknown = null) =>
    [...knowledgeKeys.all, 'access-audit', orgId, actorId, stableStringify(filters)] as const,
  /** A document's placement and readers, as its access section shows them. */
  documentAccess: (orgId: string, actorId: string | null, documentId: string) =>
    [...knowledgeKeys.all, 'document-access', orgId, actorId, documentId] as const,
  /** A document's approval for common use, as its common-use section shows it. */
  documentCommonUse: (orgId: string, actorId: string | null, documentId: string) =>
    [...knowledgeKeys.all, 'document-common-use', orgId, actorId, documentId] as const,
  /** The access dialog's own snapshot of the same, read afresh on every opening. */
  documentAccessDialog: (orgId: string, actorId: string | null, documentId: string) =>
    [...knowledgeKeys.all, 'document-access-dialog', orgId, actorId, documentId] as const,
  /** A gated document tab's title, as the viewer's RLS admits it. */
  documentTitle: (orgId: string, actorId: string | null, documentId: string) =>
    [...knowledgeKeys.all, 'document-title', orgId, actorId, documentId] as const,
  /** A draft's generation lineage at one saved revision of the draft (`updatedAt`). */
  draftLineage: (orgId: string, actorId: string | null, draftId: string, updatedAt: string) =>
    [...knowledgeKeys.all, 'draft-lineage', orgId, actorId, draftId, updatedAt] as const,
  /** A client's label, as the viewer's RLS admits it. */
  engagementLabel: (orgId: string, actorId: string | null, engagementId: string) =>
    [...knowledgeKeys.all, 'engagement-label', orgId, actorId, engagementId] as const,
  /**
   * The placement of a reply's readable sources (`documentSetKey`: their
   * sorted ids), read in a work scope: its context segment comes last, and an
   * office scope adds none.
   */
  sourcePlacement: (
    orgId: string,
    actorId: string | null,
    documentSetKey: string,
    context: KnowledgeContextScope = OFFICE_CONTEXT_SCOPE,
  ) =>
    [
      ...knowledgeKeys.all,
      'source-placement',
      orgId,
      actorId,
      documentSetKey,
      ...contextScopeSegment(context),
    ] as const,
  /**
   * The shared picker's candidates. A documents-mode key in a work context
   * ends with that context's segment; every other key has none, whatever
   * context is passed.
   */
  picker: (
    orgId: string,
    actorId: string | null,
    mode: KnowledgePickerMode,
    params: unknown = null,
    context: KnowledgeContextScope = OFFICE_CONTEXT_SCOPE,
  ) =>
    mode === 'documents'
      ? ([
          ...knowledgeKeys.all,
          'picker',
          orgId,
          actorId,
          mode,
          stableStringify(params),
          ...contextScopeSegment(context),
        ] as const)
      : ([...knowledgeKeys.all, 'picker', orgId, actorId, mode, stableStringify(params)] as const),
};

/** The office and actor a cache entry was produced for. */
export interface KnowledgeFamilyScope {
  readonly organizationId: string;
  readonly actorId: string;
}

type KnowledgeFamilyMatcher = (key: readonly unknown[], scope: KnowledgeFamilyScope) => boolean;

function startsWith(key: readonly unknown[], prefix: readonly unknown[]): boolean {
  return key.length >= prefix.length && prefix.every((part, index) => key[index] === part);
}

/** A family whose keys begin with `prefix(scope)`, which names the office and the actor. */
function byPrefix(
  prefix: (scope: KnowledgeFamilyScope) => readonly unknown[],
): KnowledgeFamilyMatcher {
  return (key, scope) => startsWith(key, prefix(scope));
}

/** A family under the knowledge root, the org at index 2 and the actor at index 3. */
function knowledgeFamily(segment: string): KnowledgeFamilyMatcher {
  return byPrefix(({ organizationId, actorId }) => [
    KNOWLEDGE_QUERY_KEY,
    segment,
    organizationId,
    actorId,
  ]);
}

/**
 * Every family whose content follows the viewer's access, selected by office
 * and actor, so an access change in one office never touches another office's
 * or another viewer's entries (ADR 0091; see
 * docs/plans/2026-10-20-feat-knowledge-spaces-library-and-access-admin-plan.md,
 * section 8). My knowledge is the `documents` and `aggregate` families under
 * the `ownerPrivate` filter. The correspondence-drafting families
 * (`source-placement`, `draft-lineage`, `engagement-label`; see
 * docs/plans/2026-10-20-feat-knowledge-spaces-correspondence-drafting-plan.md,
 * U7 and U10) and the Pulse AI gated family (`['pulse-ai', 'gated', actorId,
 * organizationId, 'messages' | 'threads' | 'sources' | 'approvals' | 'titles' |
 * 'admin-review', ...]`; see
 * docs/plans/2026-10-20-feat-knowledge-spaces-pulse-ai-client-context-plan.md)
 * are matched by the shapes those plans fix.
 */
export const KNOWLEDGE_FAMILY_MATCHERS = {
  documents: knowledgeFamily('documents'),
  'document-detail': byPrefix(({ organizationId, actorId }) => [
    ...knowledgeKeys.documentDetailAll(organizationId),
    actorId,
  ]),
  aggregate: knowledgeFamily('aggregate'),
  search: knowledgeFamily('search'),
  'work-context': knowledgeFamily('work-context'),
  spaces: knowledgeFamily('spaces'),
  space: knowledgeFamily('space'),
  'space-members': knowledgeFamily('space-members'),
  'space-sources': knowledgeFamily('space-sources'),
  engagements: knowledgeFamily('engagements'),
  associations: knowledgeFamily('associations'),
  'access-audit': knowledgeFamily('access-audit'),
  'document-access': knowledgeFamily('document-access'),
  'document-access-dialog': knowledgeFamily('document-access-dialog'),
  'document-common-use': knowledgeFamily('document-common-use'),
  'document-title': knowledgeFamily('document-title'),
  picker: knowledgeFamily('picker'),
  'source-placement': knowledgeFamily('source-placement'),
  'draft-lineage': knowledgeFamily('draft-lineage'),
  'engagement-label': knowledgeFamily('engagement-label'),
  sources: (key, { organizationId, actorId }) =>
    startsWith(key, documentKeys.all(organizationId)) && key[2] === actorId && key[3] === 'sources',
  'broadcast-ideas': (key, { organizationId, actorId }) =>
    startsWith(key, [...broadcastKeys.all(organizationId), 'ideas', actorId]),
  'draft-source-evidence': byPrefix(({ organizationId, actorId }) => [
    DRAFT_SOURCE_EVIDENCE_QUERY_KEY,
    actorId,
    organizationId,
  ]),
  'sent-draft-citations': byPrefix(({ organizationId, actorId }) => [
    SENT_DRAFT_CITATIONS_QUERY_KEY,
    actorId,
    organizationId,
  ]),
  'knowledge-storage': (key, { organizationId, actorId }) =>
    isKnowledgeStorageKey(key) && key[2] === actorId && key[3] === organizationId,
  'pulse-ai-gated': byPrefix(({ organizationId, actorId }) => [
    'pulse-ai',
    'gated',
    actorId,
    organizationId,
  ]),
} satisfies Record<string, KnowledgeFamilyMatcher>;

export type KnowledgeFamily = keyof typeof KNOWLEDGE_FAMILY_MATCHERS;

/** Whether a key belongs to any of `families` for this office and actor. */
export function matchesKnowledgeFamilies(
  key: readonly unknown[],
  scope: KnowledgeFamilyScope,
  families: readonly KnowledgeFamily[],
): boolean {
  return families.some((family) => KNOWLEDGE_FAMILY_MATCHERS[family](key, scope));
}

/**
 * Re-exported so this module's public surface is unchanged for the knowledge
 * hooks that import it from here. The implementation is shared - a second
 * copy would be free to drift on exactly the edge cases (undefined values,
 * array holes) that decide whether two callers agree on a cache key.
 */
export { stableStringify };
